import { Badge, Button, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { IconArrowRight, IconInfoCircle } from "@tabler/icons-react";
import { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  getToolBySlug,
  getToolsBySlugs,
  ToolCatalogItem,
} from "../../data/toolsCatalog";
import { ToolPageLayout, ToolTextSection } from "./shared/ToolPageLayout";

type CatalogToolInfoPageProps = {
  slug: string;
};

function CatalogToolInfoPage({ slug }: CatalogToolInfoPageProps) {
  const tool = getToolBySlug(slug);

  if (!tool) {
    return <Navigate to="/tools" replace />;
  }

  const content = createInfoContent(tool);
  const relatedTools = getToolsBySlugs(tool.relatedTools);

  return (
    <ToolPageLayout
      title={tool.title}
      description={tool.description}
      primaryKeyword={tool.primaryKeyword}
      secondaryKeywords={tool.secondaryKeywords}
      path={tool.url}
      category={tool.category}
      seoTitle={tool.seoTitle}
      metaDescription={tool.metaDescription}
      toolComponent={<InfoToolPanel tool={tool} />}
      howToUseSteps={content.howToUseSteps}
      formulaSection={content.formulaSection}
      exampleContent={content.exampleContent}
      whySection={content.whySection}
      commonMistakes={content.commonMistakes}
      faqItems={content.faqItems}
      relatedTools={relatedTools}
      relatedBlogs={tool.relatedBlogs}
      relatedServices={tool.relatedServices}
      ctaTitle={`Need help after using the ${tool.title}?`}
      ctaDescription="Share your subject, deadline, and study goal with SubjectBuddy so a tutor can help you plan the next step."
      ctaButtonText="Get Study Help"
      ctaButtonHref="/assignment-help"
    />
  );
}

function InfoToolPanel({ tool }: { tool: ToolCatalogItem }) {
  return (
    <Stack gap="lg">
      <Group gap="sm">
        <Badge color="green" radius="sm">
          {tool.category}
        </Badge>
        <Badge color="blue" radius="sm" variant="light">
          Free student tool
        </Badge>
      </Group>
      <Stack gap={6}>
        <Title order={2}>{tool.title}</Title>
        <Text c="dimmed" style={{ lineHeight: 1.8 }}>
          Use this {tool.primaryKeyword} page to review the purpose, examples,
          and related student tools before choosing the calculator or support
          page that fits your task.
        </Text>
      </Stack>
      <Paper withBorder radius="md" p="md">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <IconInfoCircle size={22} color="#2f9e44" />
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
            This page is part of the SubjectBuddy tools hub and is linked from
            related calculators so students can move between closely connected
            study tasks without losing context.
          </Text>
        </Group>
      </Paper>
      <Group gap="md">
        <Button
          component={Link}
          to="/tools"
          color="green"
          rightSection={<IconArrowRight size={18} />}
        >
          Browse All Tools
        </Button>
        <Button component={Link} to="/assignment-help" variant="light">
          Assignment Help
        </Button>
      </Group>
    </Stack>
  );
}

function createInfoContent(tool: ToolCatalogItem) {
  const categoryCopy = getCategoryCopy(tool);

  return {
    howToUseSteps: [
      `Start with the ${tool.title} summary and confirm that it matches the study task you are trying to solve.`,
      `Review the example and explanation so you understand how the ${tool.primaryKeyword} fits into your grade, writing, speech, math, or AP planning workflow.`,
      "Open the related tools section if your task needs a more specific calculator or a companion checker.",
      "Use the related student support links when you need guided help applying the result to coursework or revision.",
    ],
    formulaSection: {
      title: categoryCopy.formulaTitle,
      body: (
        <Stack gap="sm">
          <Text>{categoryCopy.formulaBody}</Text>
          <Text>
            SubjectBuddy keeps this page connected to the closest related tools
            so students can compare results, check assumptions, and move from a
            broad {tool.primaryKeyword} search to the exact calculator they
            need.
          </Text>
        </Stack>
      ),
    } as ToolTextSection,
    exampleContent: {
      title: `${tool.title} Example`,
      body: (
        <Stack gap="sm">
          <Text>{categoryCopy.exampleBody}</Text>
          <Text>
            For example, a student may start on this {tool.primaryKeyword} page,
            then follow a related link to {categoryCopy.relatedExample} for a
            more targeted calculation or writing check.
          </Text>
        </Stack>
      ),
    } as ToolTextSection,
    whySection: {
      title: `Why Students Use a ${tool.title}`,
      body: (
        <Stack gap="sm">
          <Text>
            A clear {tool.primaryKeyword} page helps students avoid guessing
            which tool to use. It explains the goal of the tool, places it
            inside a clean category, and connects it to nearby calculators and
            academic support pages.
          </Text>
          <Text>
            This matters for SEO and for real users: the page has crawlable
            links, descriptive anchor text, visible breadcrumbs, FAQ content,
            and related resources that keep the tool cluster easy to explore.
          </Text>
        </Stack>
      ),
    } as ToolTextSection,
    commonMistakes: categoryCopy.commonMistakes,
    faqItems: [
      {
        question: `What is the ${tool.title}?`,
        answer: `The ${tool.title} is a SubjectBuddy student tool page for ${tool.description.toLowerCase()}`,
      },
      {
        question: `Is this ${tool.primaryKeyword} free?`,
        answer: `Yes. This ${tool.primaryKeyword} page is free to use and is part of the SubjectBuddy tools hub.`,
      },
      {
        question: `What should I use with this ${tool.primaryKeyword}?`,
        answer: `Use the related tools on this page to continue with a closely connected calculator, checker, or study planner.`,
      },
      {
        question: "Can I get help applying the result?",
        answer:
          "Yes. SubjectBuddy links related assignment help and student support pages from each tool so you can get guidance after checking a score, draft, or calculation.",
      },
    ],
  };
}

function getCategoryCopy(tool: ToolCatalogItem): {
  formulaTitle: string;
  formulaBody: string;
  exampleBody: string;
  relatedExample: ReactNode;
  commonMistakes: string[];
} {
  if (tool.category === "Grade & GPA Tools") {
    return {
      formulaTitle: "Grade and GPA Calculation Logic",
      formulaBody:
        "Grade and GPA tools usually convert each score into a percentage or grade-point value, multiply it by the relevant weight or credit hours, and then divide by the total weight or total credits.",
      exampleBody:
        "If an assignment is worth 20% of a course and the score is 85%, its weighted contribution is 17 percentage points toward the final course grade.",
      relatedExample: "Semester Grade Calculator",
      commonMistakes: [
        "Mixing percentage weights with point totals without converting them.",
        "Forgetting that school grade scales and rounding rules can vary.",
        "Using GPA points without checking the credit hours for each course.",
      ],
    };
  }

  if (tool.category === "Essay & Writing Tools") {
    return {
      formulaTitle: "Writing Tool Explanation",
      formulaBody:
        "Writing tools help students review structure, clarity, length, and wording. They do not replace careful revision, but they make common editing checks faster and easier to repeat.",
      exampleBody:
        "A student drafting a college essay might check word count, review grammar, test title ideas, and then use an editor page to plan final revisions.",
      relatedExample: "College Essay Editor",
      commonMistakes: [
        "Treating a tool suggestion as a final edit without reading the sentence again.",
        "Changing wording so much that the original meaning becomes unclear.",
        "Ignoring assignment instructions, word limits, or citation requirements.",
      ],
    };
  }

  if (tool.category === "Speech & Presentation Tools") {
    return {
      formulaTitle: "Speech Timing Formula",
      formulaBody:
        "Speech timing tools estimate duration by dividing word count by speaking pace. A common planning range is 120 to 160 words per minute, with slower delivery for formal or emotional speeches.",
      exampleBody:
        "A 750-word speech at 150 words per minute takes about five minutes, before adding pauses, audience reactions, or slide transitions.",
      relatedExample: "Speech Time Calculator",
      commonMistakes: [
        "Planning only from word count and forgetting pauses.",
        "Speaking faster during rehearsal than during the real presentation.",
        "Ignoring slide transitions, introductions, and closing remarks.",
      ],
    };
  }

  if (tool.category === "Math Tools") {
    return {
      formulaTitle: "Math Tool Explanation",
      formulaBody:
        "Math tools apply a standard operation, show the result, and give students a quick way to check arithmetic, roots, fractions, percentages, or algebra steps.",
      exampleBody:
        "A student checking square roots might calculate a value, compare it with a percentage or fraction result, and then review the related formula page.",
      relatedExample: "Racine Carrée Calculator",
      commonMistakes: [
        "Rounding too early before the final answer.",
        "Forgetting signs, units, or exact form when a decimal is only an estimate.",
        "Skipping the step that explains why the result makes sense.",
      ],
    };
  }

  return {
    formulaTitle: "AP Score Estimate Explanation",
    formulaBody:
      "AP score tools estimate performance by converting section scores into weighted percentages and comparing the result with a 1 to 5 score band. Official score conversions can vary by exam year.",
    exampleBody:
      "A student preparing for AP exams might estimate one practice test score, then compare related AP calculators to plan revision across multiple subjects.",
    relatedExample: "AP Bio Score Calculator",
    commonMistakes: [
      "Treating an estimated AP score as an official result.",
      "Using practice-test cutoffs without checking the exam section weights.",
      "Ignoring weaker sections when the total score looks acceptable.",
    ],
  };
}

export default CatalogToolInfoPage;
